"use client";

import { type FC, useState } from "react";
import {
  Chip,
  Flex,
  GalleryCarousel,
  Image,
  Modal,
  Text,
  Tooltip,
} from "@huub-web/ui";
import { toHumanCase } from "@huub-web/utils";

import type { AnalysisObservation } from "../../../types";
import { Tip } from "../../Tip";

import {
  containerStyle,
  contentRowStyle,
  imageContainerStyle,
  imageStyle,
} from "./AnalysisListItem.css";
import { firstLetterCapitalStyle } from "../AnalysisObservationsSection.css";

export type AnalysisListItemProps = {
  observation: AnalysisObservation;
  alternate?: boolean;
  /**
   * Pre-selected image URL chosen by the parent to avoid duplicate
   * thumbnails across sibling list items. Falls back to media[0].
   */
  preferredImageUrl?: string;
};

export const AnalysisListItem: FC<AnalysisListItemProps> = ({
  observation,
  alternate = false,
  preferredImageUrl,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const media = observation.resolvedMediaReferences?.filter(
    (media) => !!media.url,
  );

  const displayMedia = preferredImageUrl
    ? (media?.find((m) => m.url === preferredImageUrl) ?? media?.[0])
    : media?.[0];

  return (
    <>
      <Flex
        direction="column"
        gap="s"
        className={containerStyle({ alternate })}
      >
        <Flex gap="s" justify="space-between" className={contentRowStyle}>
          <Flex direction="column" gap="xs" grow>
            <Flex gap="s" align="center" justify="space-between">
              <Text variant="bodyM" bold className={firstLetterCapitalStyle}>
                {observation.component?.replace(/_/g, " ")}
              </Text>
              <Chip
                variant="tertiary"
                className={firstLetterCapitalStyle}
                alignSelf="flex-start"
              >
                {observation.assessment?.replace(/_/g, " ")}
              </Chip>
            </Flex>
            <Text markdown color="currentColor50" grow>
              {observation.explanation}
            </Text>
          </Flex>

          {displayMedia && (
            <div className={imageContainerStyle}>
              <Tooltip value="Relevante media">
                <Image
                  onClick={() => setIsModalOpen(true)}
                  className={imageStyle}
                  borderRadius
                  src={displayMedia.thumbnail ?? displayMedia.url ?? ""}
                  alt={displayMedia.reference ?? ""}
                />
              </Tooltip>
            </div>
          )}
        </Flex>
        {observation.improvementAdvice && (
          <Tip name="Advies">{observation.improvementAdvice}</Tip>
        )}
      </Flex>

      {isModalOpen && (
        <Modal
          escape
          closeable
          onClose={() => setIsModalOpen(false)}
          title={
            <Flex gap="s" align="center">
              <Text variant="bodyL" bold>
                {toHumanCase(observation.component)}
              </Text>
              <Chip variant="tertiary">{observation.assessment}</Chip>
            </Flex>
          }
        >
          {media && media.length > 0 && (
            <GalleryCarousel
              items={observation.resolvedMediaReferences
                .filter((media) => !!media.url)
                .map(({ url, reference }) => ({
                  url: url ?? "",
                  reference,
                }))}
            />
          )}
          <Flex direction="column" gap="xs">
            <Text variant="bodyS" markdown>
              {observation.explanation}
            </Text>
          </Flex>
          {observation.improvementAdvice && (
            <Tip name="Advies">{observation.improvementAdvice}</Tip>
          )}
        </Modal>
      )}
    </>
  );
};
